import { useRef, useImperativeHandle, forwardRef } from 'react';
import { ArrangementList } from './ArrangementList';
import type { Arrangement } from '../types/arrangement';

export interface DecisionBoxHandle {
  insertTextAtCursor: (text: string) => void;
}

interface DecisionBoxProps {
  title?: string;
  header?: string;
  subheader?: string;
  content?: string;
  arrangements?: string;
  arrangementList?: Arrangement[];
  fontSize?: number;
  fillHeight?: boolean;
  onContentChange?: (newContent: string) => void;
  onArrangementsChange?: (newVal: string) => void;
  onArrangementChange?: (id: string, fields: Partial<Omit<Arrangement, 'id'>>) => void;
  onArrangementRemove?: (id: string) => void;
  onArrangementFocusChange?: (id: string | null, cursorPos: number) => void;
  onContentFocusChange?: (cursorPos: number) => void;
}

export const DecisionBox = forwardRef<DecisionBoxHandle, DecisionBoxProps>(({
  title,
  header,
  subheader,
  content = "",
  arrangements = "",
  arrangementList,
  fontSize = 15,
  fillHeight = false,
  onContentChange,
  onArrangementsChange,
  onArrangementChange,
  onArrangementRemove,
  onArrangementFocusChange,
  onContentFocusChange,
}, ref) => {
  const contentRef      = useRef<HTMLTextAreaElement>(null);
  const arrangementsRef = useRef<HTMLTextAreaElement>(null);
  const cursorPosRef    = useRef<number | null>(null);
  const activeFieldRef  = useRef<'content' | 'arrangements'>('content');

  useImperativeHandle(ref, () => ({
    insertTextAtCursor: (text: string) => {
      // Wstaw do ustaleń gdy subheader jest obecny (jesteśmy w trybie E3 z dyktowaniem)
      const useArrangements = !!subheader && !!onArrangementsChange;
      if (useArrangements) {
        const ta = arrangementsRef.current;
        const pos = cursorPosRef.current ?? arrangements.length;
        const before = arrangements.slice(0, pos);
        const after  = arrangements.slice(pos);
        const prefix = (before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n')) ? ' ' : '';
        const newVal = before + prefix + text + after;
        onArrangementsChange!(newVal);
        const newPos = pos + prefix.length + text.length;
        requestAnimationFrame(() => {
          ta?.setSelectionRange(newPos, newPos);
          ta?.focus();
          cursorPosRef.current = newPos;
        });
      } else {
        const ta = contentRef.current;
        if (!ta) return;
        const pos = cursorPosRef.current ?? content.length;
        const before = content.slice(0, pos);
        const after  = content.slice(pos);
        const prefix = (before.length > 0 && !before.endsWith(' ') && !before.endsWith('\n')) ? ' ' : '';
        const newContent = before + prefix + text + after;
        onContentChange?.(newContent);
        const newPos = pos + prefix.length + text.length;
        requestAnimationFrame(() => {
          ta.setSelectionRange(newPos, newPos);
          ta.focus();
          cursorPosRef.current = newPos;
        });
      }
    },
  }));

  return (
    <div
      className={`relative w-full ${fillHeight ? 'h-full' : 'h-[281px]'} rounded-[33px] backdrop-blur-[16px] border border-white/5 shadow-[inset_1px_1px_2px_rgba(255,255,255,0.15)] overflow-hidden flex flex-col p-7 mx-auto`}
      style={{
        background: 'linear-gradient(180deg, rgba(0, 255, 85, 0.08) 4.81%, rgba(0, 255, 85, 0.21) 56.33%, rgba(0, 255, 85, 0.08) 100%)'
      }}
    >
      {/* 📌 NAGŁÓWEK tytuł (stary prop) */}
      {title && (
        <div className="text-white font-semibold mb-4 tracking-wide truncate shrink-0" style={{ fontSize: `${fontSize}px` }}>
          {title}
        </div>
      )}

      {/* 📌 NAGŁÓWEK główny — Imię, data */}
      {header && (
        <div className="text-white font-semibold shrink-0" style={{ fontSize: `${fontSize + 2}px`, marginBottom: '2px' }}>
          {header}
        </div>
      )}

      {/* 📜 EDYTOWALNA STREFA TEKSTU — treść notatki */}
      <textarea
        ref={contentRef}
        value={content}
        onChange={(e) => {
          onContentChange?.(e.target.value);
          cursorPosRef.current = e.target.selectionStart;
          onContentFocusChange?.(e.target.selectionStart);
        }}
        onSelect={(e) => {
          activeFieldRef.current = 'content';
          cursorPosRef.current = (e.target as HTMLTextAreaElement).selectionStart;
          onContentFocusChange?.((e.target as HTMLTextAreaElement).selectionStart);
        }}
        onFocus={(e) => {
          activeFieldRef.current = 'content';
          cursorPosRef.current = e.target.selectionStart;
          onContentFocusChange?.(e.target.selectionStart);
        }}
        onBlur={(e) => {
          cursorPosRef.current = e.target.selectionStart;
        }}
        className="flex-1 bg-transparent resize-none outline-none custom-scrollbar text-[#B3B3B3] font-light leading-relaxed text-justify whitespace-pre-wrap pr-2"
        style={{ fontSize: `${fontSize - 2}px`, minHeight: '80px' }}
        spellCheck={false}
      />

      {/* 📌 ŚRÓDTYTUŁ — Co ustaliliście? — separator przed strefą ustaleń */}
      {subheader && (
        <>
          <div className="text-white font-semibold shrink-0" style={{ fontSize: `${fontSize}px`, marginTop: '10px', marginBottom: '6px', letterSpacing: '0.3px' }}>
            {subheader}
          </div>
          {/* 📜 STREFA USTALEŃ — lista ArrangementList (e3) z maskowaniem i scroll */}
          {arrangementList !== undefined ? (
            <div 
              className="arrangements-scroll-container"
              style={{ 
                position: 'relative',
                height: '240px', // Wysokość na 2 kafelki (ok. 110px + 10px gap each)
                overflow: 'hidden',
                marginTop: '6px',
                marginBottom: '0',
              }}
            >
              {/* Scrollable wrapper - bez scrollbarów */}
              <div 
                className="arrangements-scroll-content"
                style={{
                  height: '100%',
                  overflowY: 'auto',
                  overflowX: 'hidden',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  paddingTop: '15px',
                  paddingBottom: '15px',
                  paddingLeft: '7px',
                  paddingRight: '7px',
                }}
              >
                <ArrangementList
                  items={arrangementList}
                  mode="e3"
                  onChange={onArrangementChange}
                  onRemove={onArrangementRemove}
                  onFocusChange={onArrangementFocusChange}
                />
              </div>
            </div>
          ) : (
            <textarea
              ref={arrangementsRef}
              value={arrangements}
              onChange={(e) => {
                onArrangementsChange?.(e.target.value);
                cursorPosRef.current = e.target.selectionStart;
              }}
              onSelect={(e) => {
                activeFieldRef.current = 'arrangements';
                cursorPosRef.current = (e.target as HTMLTextAreaElement).selectionStart;
              }}
              onFocus={() => {
                activeFieldRef.current = 'arrangements';
                cursorPosRef.current = arrangements.length;
                setTimeout(() => {
                  if (arrangementsRef.current && document.activeElement === arrangementsRef.current) {
                    arrangementsRef.current.blur();
                  }
                }, 100);
              }}
              onBlur={(e) => { cursorPosRef.current = e.target.selectionStart; }}
              inputMode="none"
              autoCapitalize="off"
              autoComplete="off"
              autoCorrect="off"
              className="bg-transparent resize-none outline-none custom-scrollbar text-[#B3B3B3] font-light leading-relaxed text-justify whitespace-pre-wrap pr-2"
              style={{ fontSize: `${fontSize - 2}px`, minHeight: '3.5em', maxHeight: '8em' }}
              spellCheck={false}
            />
          )}
        </>
      )}

      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 3px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 99px; transition: background 0.2s ease; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
        .arrangements-scroll-content::-webkit-scrollbar { display: none; }
        .arrangements-scroll-content { -ms-overflow-style: none; scrollbar-width: none; }
        textarea::placeholder { color: rgba(255,255,255,0.18); }
      `}</style>
    </div>
  );
});